import * as React from 'react';
import styled from 'styled-components';
import * as classnames from 'classnames';
import { observer } from 'mobx-react';
import { ContentSpacing, breakpoint } from 'elc-base-theme';
import { Heading5, Body2 } from 'elc-typography';
import { translate, ITranslationsCollection } from 'elc-service';
import { Product } from '../../domain/entities/Product';
import { FINISH } from '../../constants/FinishOptions';
import { IMapping } from '../../../exported/product-cta/ProductCTA';
import { COVERAGE } from '../../constants/CoverageOptions';
import { SKINTYPE } from '../../constants/SkinTypeOptions';
import { classes } from '../../utils/Classes';
import { PRODUCT_OVERVIEW_SUMMARY } from '../../constants/DataTestIdAttributes';

export interface IProductOverviewSummary {
    translations?: ITranslationsCollection;
    product: Product;
    className?: string;
}

const Wrapper = styled.div`
    width: 100%;
    display: block;
    clear: both;
    padding: ${ContentSpacing.space12} ${ContentSpacing.space8};
    margin: ${ContentSpacing.space12} 0;
`;

const SummarySection = styled.div`
    width: 100%;
    float: left;
    [dir='rtl'] & {
        float: right;
    }

    ${breakpoint('desktop')`
        width: 50%;
    `};
`;

interface IOverviewSection {
    headingLabel: string;
    content: string;
    className?: string;
}

const OverviewSection = (props: IOverviewSection) => {
    const { headingLabel, content } = props;

    const productOverviewSummarySectionClassNames = classes('product-overview-summary-section');
    const productOverviewSummarySectionHeaderClassNames = classes(
        'product-overview-summary-section-header'
    );
    const productOverviewSummarySectionBodyClassNames = classes(
        'product-overview-summary-section-body'
    );

    return (
        <SummarySection className={productOverviewSummarySectionClassNames}>
            <Heading5 className={productOverviewSummarySectionHeaderClassNames}>
                {headingLabel}
            </Heading5>
            <Body2
                className={productOverviewSummarySectionBodyClassNames}
                dangerouslySetInnerHTML={{ __html: content }}
            />
        </SummarySection>
    );
};

export const ProductOverviewSummary = translate([
    'benefitsLabel',
    'finishLabel',
    'coverageLabel',
    'keyIngredientsLabel',
    'skinTypeLabel',
    'matteFinishLabel',
    'satinFinishLabel',
    'shineFinishLabel',
    'naturalFinishLabel',
    'shimmerFinishLabel',
    'sheerCoverageLabel',
    'mediumCoverageLabel',
    'fullCoverageLabel',
    'sensitiveSkinTypeLabel',
    'veryDrySkinTypeLabel',
    'drySkinTypeLabel',
    'antiAgingSkinTypeLabel',
    'combinationSkinTypeLabel',
    'oilySkinTypeLabel',
    'normalSkinTypeLabel'
])(
    observer((props: IProductOverviewSummary) => {
        const productOverviewSummaryClassNames = classnames(
            'elc-product-overview-summary',
            'js-product-overview-summary'
        );

        const {
            benefitsLabel,
            finishLabel,
            coverageLabel,
            keyIngredientsLabel,
            skinTypeLabel,
            matteFinishLabel,
            satinFinishLabel,
            shineFinishLabel,
            naturalFinishLabel,
            shimmerFinishLabel,
            sheerCoverageLabel,
            mediumCoverageLabel,
            fullCoverageLabel,
            sensitiveSkinTypeLabel,
            veryDrySkinTypeLabel,
            drySkinTypeLabel,
            antiAgingSkinTypeLabel,
            combinationSkinTypeLabel,
            oilySkinTypeLabel,
            normalSkinTypeLabel
        } = props.translations as ITranslationsCollection;

        const {
            benefitStatement,
            finishKeys,
            coverageKeys,
            keyIngredient,
            skinTypeKeys
        } = props.product;

        const finishLabels: IMapping = {
            [FINISH.MATTE]: matteFinishLabel,
            [FINISH.SATIN]: satinFinishLabel,
            [FINISH.SHINE]: shineFinishLabel,
            [FINISH.NATURAL]: naturalFinishLabel,
            [FINISH.SHIMMER]: shimmerFinishLabel
        };

        const coverageLabels: IMapping = {
            [COVERAGE.SHEER]: sheerCoverageLabel,
            [COVERAGE.MEDIUM]: mediumCoverageLabel,
            [COVERAGE.FULL]: fullCoverageLabel
        };

        const skinTypeLabels: IMapping = {
            [SKINTYPE.SENSITIVE]: sensitiveSkinTypeLabel,
            [SKINTYPE.VERYDRY]: veryDrySkinTypeLabel,
            [SKINTYPE.DRY]: drySkinTypeLabel,
            [SKINTYPE.ANTIAGING]: antiAgingSkinTypeLabel,
            [SKINTYPE.COMBINATION]: combinationSkinTypeLabel,
            [SKINTYPE.OILY]: oilySkinTypeLabel,
            [SKINTYPE.NORMAL]: normalSkinTypeLabel
        };

        const finishValues = finishKeys
            ? finishKeys.map(finishKey => finishLabels[finishKey] || finishKey).join(', ')
            : '';

        const coverageValues = coverageKeys
            ? coverageKeys.map(coverageKey => coverageLabels[coverageKey] || coverageKey).join(', ')
            : '';

        const skinTypeValues = skinTypeKeys
            ? skinTypeKeys.map(skinTypeKey => skinTypeLabels[skinTypeKey] || skinTypeKey).join(', ')
            : '';

        const sectionContents = [
            [benefitsLabel, benefitStatement],
            [finishLabel, finishValues],
            [coverageLabel, coverageValues],
            [keyIngredientsLabel, keyIngredient],
            [skinTypeLabel, skinTypeValues]
        ];

        const sections = sectionContents.map((sectionContent, index) => {
            const [label, content] = sectionContent;

            return (
                content && (
                    <OverviewSection
                        headingLabel={label as string}
                        content={content as string}
                        className={props.className}
                        key={`${index}-${label}`}
                    />
                )
            );
        });

        return (
            <Wrapper
                data-test-id={PRODUCT_OVERVIEW_SUMMARY}
                className={productOverviewSummaryClassNames}
            >
                {sections}
            </Wrapper>
        );
    })
);
